import { useEffect, useMemo, useState } from "react";
import { useSearchProducts, type SearchProductRow } from "./use-search-products";
import type { SearchIndex, SearchOutcome } from "@/lib/search-index";

// Το ευρετήριο (~330 γραμμές λογικής) φορτώνεται δυναμικά μόνο όταν χρειαστεί,
// ώστε να μη χρεώνεται στο κύριο πακέτο/νήμα κατά την αρχική φόρτωση.
type SearchModule = typeof import("@/lib/search-index");
let searchModule: SearchModule | null = null;
let searchModulePromise: Promise<SearchModule> | null = null;
function loadSearchModule(): Promise<SearchModule> {
  searchModulePromise ??= import("@/lib/search-index").then((m) => (searchModule = m));
  return searchModulePromise;
}

/**
 * Κοινόχρηστο (module-level) cache ώστε το ευρετήριο να χτίζεται ΜΙΑ φορά για
 * όλο το app — ακόμη κι αν το hook χρησιμοποιείται ταυτόχρονα σε Header + /search.
 * Το κλειδί είναι η ίδια η αναφορά του πίνακα προϊόντων (react-query cache).
 */
const indexCache = new WeakMap<object, SearchIndex<SearchProductRow>>();

function getSharedIndex(
  products: SearchProductRow[],
  mod: SearchModule,
): SearchIndex<SearchProductRow> {
  const cached = indexCache.get(products);
  if (cached) return cached;
  const built = mod.buildSearchIndex(products);
  indexCache.set(products, built);
  return built;
}

/**
 * Χτίζει (μία φορά) το ευρετήριο αναζήτησης πάνω στον κατάλογο και επιστρέφει
 * μια συνάρτηση `run(query)` που τρέχει σε λίγα ms ακόμη και σε 18.000+ προϊόντα.
 */
export function useSearchIndex(enabled: boolean = true) {
  const { products, isReady, error } = useSearchProducts(enabled);
  const [mod, setMod] = useState<SearchModule | null>(searchModule);

  useEffect(() => {
    if (!enabled || mod) return;
    let active = true;
    loadSearchModule().then((m) => {
      if (active) setMod(m);
    });
    return () => {
      active = false;
    };
  }, [enabled, mod]);

  const index = useMemo<SearchIndex<SearchProductRow> | null>(() => {
    if (!isReady || products.length === 0 || !mod) return null;
    return getSharedIndex(products, mod);
  }, [products, isReady, mod]);

  const run = useMemo(
    () =>
      (query: string, limit = 500): SearchOutcome<SearchProductRow> => {
        if (!index || !mod) return { items: [], mode: "empty", didYouMean: null, brands: [], categories: [] };
        return mod.searchIndex(index, query, { limit });
      },
    [index, mod],
  );

  return { index, run, products, isReady: isReady && !!index, error };
}

/** Debounce helper (χωρίς εξωτερική εξάρτηση). */
export function useDebounced<T>(value: T, delay = 140): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}
